import { Box, Button } from '@mui/material';
import React from 'react';
import {
  FileInput,
  FileInputProps,
  Loading,
  setSubmissionErrors,
  useInput,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Prefix } from '../../../graphql/types';
import { useUploadFile } from '../../../hooks/useUploadFile';

interface VideoUploadInputProps extends FileInputProps {
  prefix: Prefix;
  onFrameCapture?: (dataUrl: string) => void;
}

const VideoUploadInput: React.FC<VideoUploadInputProps> = ({
  prefix,
  source,
  onFrameCapture,
  ...props
}) => {
  const [loading, setLoading] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const { setError } = useFormContext();
  const { field, fieldState } = useInput({ source });
  const { uploadVideo } = useUploadFile();

  const captureFrame = React.useCallback(() => {
    const video = videoRef.current;
    if (!video || !onFrameCapture) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/webp', 0.9);
    onFrameCapture(dataUrl);
  }, [onFrameCapture]);

  const handleChange = React.useCallback(
    async (value?: File) => {
      if (value instanceof File) {
        setLoading(true);
        setSubmissionErrors({ [source]: '' }, setError);

        let key: string | null = null;
        let url: string | null = null;
        try {
          const result = await uploadVideo(value, prefix);
          key = result.key;
          url = result.url;
        } catch (error) {
          if (error instanceof Error) {
            setSubmissionErrors({ [source]: error.message }, setError);
          } else {
            setSubmissionErrors(
              { [source]: 'Unexpected error during video upload' },
              setError,
            );
          }
          setLoading(false);
          return;
        }

        // In order to support video preview as well as sending the correct value for `video`
        // we need to treat a video field as an object within React-Admin
        // `key` is what is stored in the database/passed into mutations
        // `src` is what is used for the video preview
        if (prefix === Prefix.ExerciseVideo) {
          field.onChange({
            key: key && key.includes('/') ? key.split('/')[1] : null,
            url,
          }); // get ID from Exercise Video key
        } else {
          field.onChange({
            key: key,
            url,
          });
        }
      } else {
        field.onChange(null);
      }
      setLoading(false);
    },
    [uploadVideo, field, prefix, setError, source],
  );
  return (
    <FileInput
      source={source}
      onChange={handleChange}
      accept={{
        'video/mp4': ['.mp4'],
      }}
      {...props}
    >
      <div>
        {loading && <Loading />}
        {!loading && !fieldState.error?.message && field.value?.url && (
          <Box flexDirection={'column'} display="flex">
            {/* biome-ignore lint/a11y/useMediaCaption: these videos don't have dialogue */}
            <video
              ref={videoRef}
              src={field.value?.url}
              controls
              crossOrigin="anonymous"
              style={{ width: '100%', maxWidth: 480, maxHeight: 480 }}
            />
            {onFrameCapture && (
              <Button
                variant="contained"
                onClick={captureFrame}
                sx={{ maxWidth: 400, m: 2 }}
                size="small"
                disabled={!field.value?.url}
              >
                Capture Thumbnail from Current Video Frame
              </Button>
            )}
          </Box>
        )}
      </div>
    </FileInput>
  );
};

export default VideoUploadInput;
